import {
  InsuranceTerm,
  PossessionBaseEnum,
  VehicleType,
  ZoneEnum
} from '@insurance/api/requests'
import { ConditionalText, DateTimeCell } from '@shared/components'
import { Form } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  InsuranceRcaDescriptionItem,
  InsuranceRcaDetails
} from '../InsuranceRcaDetails'
import { useStyles } from './styles'
import { formProps, zoneLabelMap } from './utils'

export const Details = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const {
    identificationNumberProps,
    certificateNumberProps,
    trailerCertificateNumberProps,
    zoneProps,
    termProps,
    validFromProps,
    vehicleTypeProps,
    possessionBaseProps,
    possessionBaseDocNumberProps,
    possessionBaseDocDateProps,
    possessionBaseDocOtherTitleProps,
    possessionBaseDocNoteProps
  } = formProps(t)

  const identificationNumber = Form.useWatch(identificationNumberProps.name, {
    preserve: true
  })
  const certificateNumber = Form.useWatch(certificateNumberProps.name, {
    preserve: true
  })
  const trailerCertificateNumber = Form.useWatch(
    trailerCertificateNumberProps.name,
    { preserve: true }
  )
  const zone = Form.useWatch(zoneProps.name, { preserve: true })
  const term = Form.useWatch(termProps.name, { preserve: true })
  const validFrom = Form.useWatch(validFromProps.name, { preserve: true })
  const vehicleType = Form.useWatch(vehicleTypeProps.name, { preserve: true })
  const possessionBase = Form.useWatch<string>(possessionBaseProps.name, {
    preserve: true
  })
  const possessionBaseDocNumber = Form.useWatch(
    possessionBaseDocNumberProps.name,
    { preserve: true }
  )
  const possessionBaseDocDate = Form.useWatch(possessionBaseDocDateProps.name, {
    preserve: true
  })
  const possessionBaseDocOtherTitle = Form.useWatch(
    possessionBaseDocOtherTitleProps.name,
    { preserve: true }
  )
  const possessionBaseDocNote = Form.useWatch(possessionBaseDocNoteProps.name, {
    preserve: true
  })

  const showPossessionBaseDocFields =
    !!possessionBase && possessionBase !== PossessionBaseEnum.PROPERTY

  const items: InsuranceRcaDescriptionItem[] = [
    {
      label: identificationNumberProps.label,
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!identificationNumber}
        >
          {identificationNumber}
        </ConditionalText>
      )
    },
    {
      label:
        vehicleType === VehicleType.TRAILER
          ? t('towing_vehicle_registration_certificate')
          : t('vehicle_registration_certificate'),
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!certificateNumber}
        >
          {certificateNumber}
        </ConditionalText>
      ),
      hidden: !certificateNumber
    },
    {
      label: trailerCertificateNumberProps.label,
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!trailerCertificateNumber}
        >
          {trailerCertificateNumber}
        </ConditionalText>
      ),
      hidden: vehicleType === VehicleType.VEHICLE
    },
    {
      label: zoneProps.label,
      children: (
        <ConditionalText className={styles.text} condition={!!zone}>
          {t(zoneLabelMap[zone as ZoneEnum] || '')}
        </ConditionalText>
      ),
      hidden: !zone
    },
    {
      label: termProps.label,
      children: (
        <ConditionalText className={styles.text} condition={!!term}>
          {term ? t(term as InsuranceTerm) : ''}
        </ConditionalText>
      ),
      hidden: !term
    },
    {
      label: validFromProps.label,
      children: (
        <DateTimeCell
          date={validFrom}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: !validFrom
    },
    {
      label: possessionBaseProps.label,
      children: (
        <ConditionalText className={styles.text} condition={!!possessionBase}>
          {t(possessionBase || '')}
        </ConditionalText>
      ),
      hidden: !possessionBase
    },
    {
      label: possessionBaseDocNumberProps.label,
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!possessionBaseDocNumber}
        >
          {possessionBaseDocNumber}
        </ConditionalText>
      ),
      hidden: !showPossessionBaseDocFields || !possessionBaseDocNumber
    },
    {
      label: possessionBaseDocDateProps.label,
      children: (
        <DateTimeCell
          date={possessionBaseDocDate}
          hiddenTime
          dateProps={{ className: styles.date }}
        />
      ),
      hidden: !showPossessionBaseDocFields || !possessionBaseDocDate
    },
    {
      label: possessionBaseDocOtherTitleProps.label,
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!possessionBaseDocOtherTitle}
        >
          {possessionBaseDocOtherTitle}
        </ConditionalText>
      ),
      hidden: !showPossessionBaseDocFields || !possessionBaseDocOtherTitle
    },
    {
      label: possessionBaseDocNoteProps.label,
      children: (
        <ConditionalText
          className={styles.text}
          condition={!!possessionBaseDocNote}
        >
          {possessionBaseDocNote}
        </ConditionalText>
      ),
      hidden: !showPossessionBaseDocFields || !possessionBaseDocNote
    }
  ].filter(({ hidden }) => !hidden)

  return <InsuranceRcaDetails items={items} />
}
